import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import type { AuthError } from '@supabase/supabase-js';
import { retry } from '../utils/async';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: {
      getItem: (key) => {
        try {
          return localStorage.getItem(key);
        } catch (error) {
          console.error('Error accessing localStorage:', error);
          return null;
        }
      },
      setItem: (key, value) => {
        try {
          localStorage.setItem(key, value);
        } catch (error) {
          console.error('Error setting localStorage:', error);
        }
      },
      removeItem: (key) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error('Error removing from localStorage:', error);
        }
      }
    }
  },
  global: {
    headers: {
      'Cache-Control': 'no-store',
      'X-Client-Info': 'supabase-js/2.39.7'
    }
  }
});

// Type guard for auth errors
export const isAuthError = (error: unknown): error is AuthError => {
  return !!(
    error && 
    typeof error === 'object' && 
    ('__isAuthError' in error || 'status' in error)
  );
};

// Sign in with email and password
export async function signIn(email: string, password: string) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
      options: {
        redirectTo: window.location.origin
      }
    });

    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    console.error('Sign in error:', err);
    if (isAuthError(err)) {
      if (err.message.includes('Invalid login credentials')) {
        throw new Error('Invalid email or password');
      } else if (err.status === 500) {
        throw new Error('Service temporarily unavailable. Please try again later.');
      } else if (err.message.includes('Email not confirmed')) {
        throw new Error('Please verify your email before signing in');
      }
      throw err;
    }
    throw new Error('An unexpected error occurred');
  }
}

// Sign up with email and password
export async function signUp(email: string, password: string, metadata = {}) {
  try {
    // First check if user already exists
    const { data: existingUser } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', email.toLowerCase())
      .single();

    if (existingUser) {
      throw new Error('An account with this email already exists');
    }

    // Proceed with signup
    const { data, error } = await retry(() => 
      supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: {
            ...metadata,
            email: email.trim().toLowerCase(),
            name: metadata.name || email.split('@')[0],
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        }
      }),
      3,
      1000
    );

    if (error) throw error;
    
    // Wait briefly to allow profile creation to complete
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return { data, error: null };
  } catch (err) {
    console.error('Sign up error:', err);
    if (isAuthError(err)) {
      if (err.message.includes('User already registered')) {
        throw new Error('An account with this email already exists');
      } else if (err.status === 500) {
        throw new Error('Service temporarily unavailable. Please try again later.');
      }
      throw err;
    }
    throw new Error('An unexpected error occurred');
  }
}

// Sign out
export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    // Clear all auth state
    localStorage.clear();
    sessionStorage.clear();
  } catch (err) {
    console.error('Sign out error:', err);
    if (isAuthError(err)) {
      if (err.message.includes('Invalid login credentials')) {
        throw new Error('Invalid email or password');
      } else if (err.status === 500) {
        throw new Error('Service temporarily unavailable. Please try again later.');
      } else if (err.message.includes('Email not confirmed')) {
        throw new Error('Please verify your email before signing in');
      }
      throw err;
    }
    throw new Error('An unexpected error occurred');
  }
}

// Get current session
export async function getCurrentSession() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  } catch (err) {
    console.error('Get session error:', err);
    return null;
  }
}

// Auth state change handler
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_IN') {
    // Handle successful sign in
  } else if (event === 'SIGNED_OUT') {
    // Clear auth state and redirect
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/auth';
  } else if (event === 'USER_UPDATED') {
    // Handle user data updates
  }
});